import axios from "axios";
import authHeader from "./auth-header";
import AuthService from "./auth.service";
import { config } from "../config";

class InternalStateService {
  /**
   * Get all internal states
   */
  getAllInternalStates() {
    return axios
      .get(`${config["apiUrl"]}internal-states`, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        throw new Error("Failed to fetch all internal states");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to fetch all internal states");
      });
  }

  /**
   * Get internal states by parent status
   */
  getByParentStatus(parentStatusId) {
    return axios
      .get(`${config["apiUrl"]}internal-states/parent/${parentStatusId}`, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        throw new Error("Failed to fetch internal states by parent status");
      })
      .catch((error) => {
        if (error.response && error.response.status == 403) {
          return [];
        }
        if (error.response && error.response.status == 401) {
          throw error;
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to fetch internal states by parent status");
      });
  }

  /**
   * Get available internal states for a PAT
   */
  getAvailableStatesForPat(nopat) {
    return axios
      .get(`${config["apiUrl"]}internal-states/pat/${nopat}/available`, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        throw new Error("Failed to fetch available internal states for PAT");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to fetch available internal states for PAT");
      });
  }

  /**
   * Create new internal state
   */
  createInternalState(bodyParams) {
    return axios
      .post(`${config["apiUrl"]}internal-states`, bodyParams, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200 || response.status == 201) {
          return response.data;
        }
        throw new Error("Failed to create internal state");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to create internal state");
      });
  }

  /**
   * Update internal state
   */
  updateInternalState(id, bodyParams) {
    return axios
      .put(`${config["apiUrl"]}internal-states/${id}`, bodyParams, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }

        throw new Error("Failed to update internal state");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to update internal state");
      });
  }

  /**
   * Delete internal state
   */
  deleteInternalState(id) {
    return axios
      .delete(`${config["apiUrl"]}internal-states/${id}`, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        throw new Error("Failed to delete internal state");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to delete internal state");
      });
  }

  /**
   * Reorder internal states
   */
  reorderInternalStates(bodyParams) {
    return axios
      .post(`${config["apiUrl"]}internal-states/reorder`, bodyParams, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        throw new Error("Failed to reorder internal states");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to reorder internal states");
      });
  }

  /**
   * Toggle active status
   */
  toggleActive(id) {
    return axios
      .patch(`${config["apiUrl"]}internal-states/${id}/toggle-active`, {}, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        throw new Error("Failed to toggle active status");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to toggle active status");
      });
  }

  /**
   * Change PAT internal state
   */
  changePatInternalState(bodyParams) {
    return axios
      .post(`${config["apiUrl"]}pats/change-internal-state`, bodyParams, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200 || response.status == 201) {
          return response.data;
        }
        throw new Error("Failed to toggle active status");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to toggle active status");
      });
  }

  /**
   * Get PAT internal state history
   */
  getPatInternalStateHistory(nopat) {
    return axios
      .get(`${config["apiUrl"]}pats/${nopat}/internal-state-history`, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        throw new Error("Failed to fetch PAT internal state history");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to fetch PAT internal state history");
      });
  }

  /**
   * Get current PAT internal state
   */
  getCurrentPatInternalState(nopat) {
    return axios
      .get(`${config["apiUrl"]}pats/${nopat}/current-internal-state`, {
        headers: authHeader(),
      })
      .then((response) => {
        if (response.status == 200) {
          return response.data;
        }
        throw new Error("Failed to fetch current PAT internal state");
      })
      .catch((error) => {
        if (error.response && error.response.status == 401) {
          AuthService.logout();
          window.location = "/login";
        }
        console.log("ERROR:: ", error.response?.data || error);
        throw new Error("Failed to fetch current PAT internal state");
      });
  }
}

export default new InternalStateService();
